import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';

interface Tier {
  id: number;
  name: string;
  price: number;
  maxVodDuration: number;
  dailyVodUploadLimit: number;
  weeklyVodUploadLimit: number;
  monthlyVodUploadLimit: number;
  maxClipsPerVod: number;
  ffmpegResolution: string;
  ffmpegFps: number;
  editorResolution: string;
  editorFps: number;
  customAiPrompt: boolean;
  monthlyRedetectionLimit: number;
}

interface Subscription {
  id: number;
  userId: number;
  tierId: number;
  tier: Tier;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'PENDING';
  startDate: string;
  endDate: string;
  autoRenew: boolean;
}

interface UsageInfo {
  limits: {
    dailyVodUploadLimit: number;
    weeklyVodUploadLimit: number;
    monthlyVodUploadLimit: number;
    maxClipsPerVod: number;
    monthlyRedetectionLimit: number;
  };
  currentUsage: {
    dailyVodsUploaded: number;
    weeklyVodsUploaded: number;
    monthlyVodsUploaded: number;
    monthlyRedetectionsUsed: number;
  };
  remaining: {
    dailyVods: number;
    weeklyVods: number;
    monthlyVods: number;
    monthlyRedetections: number;
  };
}

interface SubscriptionContextType {
  tiers: Tier[];
  activeSubscription: Subscription | null;
  usageInfo: UsageInfo | null;
  isLoading: boolean;
  fetchTiers: () => Promise<void>;
  fetchActiveSubscription: () => Promise<void>;
  fetchUsageStats: () => Promise<void>;
  createSubscription: (tierId: number) => Promise<{ success: boolean; message?: string; paymentId?: string }>;
  verifyPayment: (paymentId: string) => Promise<{ success: boolean; message?: string }>;
  cancelSubscription: (subscriptionId: number) => Promise<{ success: boolean; message?: string }>;
  changeTier: (subscriptionId: number, newTierId: number, immediate?: boolean) => Promise<{ success: boolean; message?: string }>;
  checkFeatureAccess: (feature: string, params?: Record<string, any>) => Promise<{ allowed: boolean; message?: string; currentUsage?: number; limit?: number; remaining?: number }>;
  incrementClipCount: (count?: number) => Promise<{ success: boolean; message?: string }>;
  incrementVodCount: () => Promise<{ success: boolean; message?: string }>;
  getFfmpegSettings: () => Promise<{ resolution: string; fps: number }>;
  getEditorSettings: () => Promise<{ resolution: string; fps: number }>;
  getGpuUsageCount: () => number;
  incrementGpuUsage: () => Promise<void>;
  freeGpuJobsUsed: number;
  fetchFreeGpuJobsUsed: () => Promise<void>;
  getFreeGpuLimit: () => number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [activeSubscription, setActiveSubscription] = useState<Subscription | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tier1GpuUsageCount, setTier1GpuUsageCount] = useState<number>(0);
  const [freeGpuJobsUsed, setFreeGpuJobsUsed] = useState<number>(0);

  const FREE_TIER_GPU_LIMIT = 3;

  const fetchTiers = useCallback(async () => {
    try {
      const response = await api.get('/tiers');
      if (response.data.success) {
        setTiers(response.data.tiers);
      }
    } catch (error) {
      console.error('Tier fetch error:', error);
    }
  }, []);

  const fetchActiveSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setActiveSubscription(null);
      return;
    }
    try {
      const response = await api.get('/subscriptions/active');
      if (response.data.success) {
        setActiveSubscription(response.data.subscription);
      } else {
        setActiveSubscription(null);
      }
    } catch (error) {
      console.error('Active subscription fetch error:', error);
      setActiveSubscription(null);
    }
  }, [isAuthenticated]);

  const fetchUsageStats = useCallback(async () => {
    if (!isAuthenticated) {
      setUsageInfo(null);
      return;
    }
    try {
      const cacheBuster = `_cb=${new Date().getTime()}`;
      const response = await api.get(`/subscriptions/usage?${cacheBuster}`);
      if (response.data.success) {
        setUsageInfo({
          limits: response.data.limits,
          currentUsage: response.data.currentUsage,
          remaining: response.data.remaining
        });
      } else {
        setUsageInfo(null);
      }
    } catch (error) {
      console.error('Usage stats fetch error:', error);
      setUsageInfo(null);
    }
  }, [isAuthenticated]);

  const fetchFreeGpuJobsUsed = useCallback(async () => {
    if (!isAuthenticated) {
      setFreeGpuJobsUsed(0);
      return;
    }
    if (isAuthenticated && !activeSubscription) {
        try {
            const response = await api.get('/user/gpu-usage');
            if (response.data.success) {
                setFreeGpuJobsUsed(response.data.gpuUsageCount || 0);
            } else {
                setFreeGpuJobsUsed(0);
            }
        } catch (error) {
            console.error('Free GPU jobs used fetch error:', error);
            setFreeGpuJobsUsed(0);
        }
    } else {
        setFreeGpuJobsUsed(0);
    }
  }, [isAuthenticated, activeSubscription]);

  const createSubscription = useCallback(async (tierId: number) => {
    try {
      const response = await api.post('/subscriptions', { tierId, autoRenew: true });
      if (response.data.success) {
        await fetchActiveSubscription();
        await fetchUsageStats();
        return { success: true, message: response.data.message, paymentId: response.data.payment?.paymentId };
      }
      return { success: false, message: response.data.message || 'Subscription oluşturulamadı.' };
    } catch (error: any) {
      console.error('Subscription creation error:', error);
      return { success: false, message: error.response?.data?.message || 'Subscription oluşturulurken bir hata oluştu.' };
    }
  }, [fetchActiveSubscription, fetchUsageStats]);

  const verifyPayment = useCallback(async (paymentId: string) => {
    try {
      const response = await api.get(`/subscriptions/verify-payment/${paymentId}`);
      if (response.data.success) {
        await fetchActiveSubscription();
        await fetchUsageStats();
        return { success: true, message: response.data.message };
      }
      return { success: false, message: response.data.message || 'Ödeme doğrulanamadı.' };
    } catch (error: any) {
      console.error('Payment verification error:', error);
      return { success: false, message: error.response?.data?.message || 'Ödeme doğrulanırken bir hata oluştu.' };
    }
  }, [fetchActiveSubscription, fetchUsageStats]);

  const cancelSubscription = useCallback(async (subscriptionId: number) => {
    try {
      const response = await api.post('/subscriptions/cancel', { subscriptionId });
      if (response.data.success) {
        await fetchActiveSubscription();
        await fetchUsageStats();
        return { success: true, message: response.data.message };
      }
      return { success: false, message: response.data.message || 'Subscription iptal edilemedi.' };
    } catch (error: any) {
      console.error('Subscription cancellation error:', error);
      return { success: false, message: error.response?.data?.message || 'Subscription iptal edilirken bir hata oluştu.' };
    }
  }, [fetchActiveSubscription, fetchUsageStats]);

  const changeTier = useCallback(async (subscriptionId: number, newTierId: number, immediate: boolean = false) => {
    try {
      const response = await api.post('/subscriptions/change-tier', { subscriptionId, newTierId, immediate });
      if (response.data.success) {
        await fetchActiveSubscription();
        await fetchUsageStats();
        return { success: true, message: response.data.message };
      }
      return { success: false, message: response.data.message || 'Subscription planı değiştirilemedi.' };
    } catch (error: any) {
      console.error('Tier change error:', error);
      return { success: false, message: error.response?.data?.message || 'Subscription planı değiştirilirken bir hata oluştu.' };
    }
  }, [fetchActiveSubscription, fetchUsageStats]);

  const checkFeatureAccess = useCallback(async (feature: string, params: Record<string, any> = {}) => {
    if (!isAuthenticated) {
      return { allowed: false, message: 'Bu özelliği kullanmak için giriş yapmalısınız.' };
    }
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => queryParams.append(key, String(value)));
      const url = `/subscriptions/check-access/${feature}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Feature access check error:', error);
      return { allowed: false, message: error.response?.data?.message || 'Özellik erişimi kontrol edilirken bir hata oluştu.' };
    }
  }, [isAuthenticated]);

  const incrementClipCount = useCallback(async (count: number = 1) => {
    if (!isAuthenticated) return { success: false, message: 'Klip oluşturmak için giriş yapmalısınız.' };
    try {
      const response = await api.post('/subscriptions/increment-clip', { count });
      if (response.data.success) {
        await fetchUsageStats();
        return { success: true, message: response.data.message };
      }
      return { success: false, message: response.data.message || 'Klip sayısı artırılamadı.' };
    } catch (error: any) {
      console.error('Clip count increment error:', error);
      return { success: false, message: error.response?.data?.message || 'Klip sayısı artırılırken bir hata oluştu.' };
    }
  }, [isAuthenticated, fetchUsageStats]);

  const incrementVodCount = useCallback(async () => {
    if (!isAuthenticated) return { success: false, message: 'VOD yüklemek için giriş yapmalısınız.' };
    try {
      const response = await api.post('/subscriptions/increment-vod', {});
      if (response.data.success) {
        await fetchUsageStats();
        return { success: true, message: response.data.message };
      }
      return { success: false, message: response.data.message || 'VOD sayısı artırılamadı.' };
    } catch (error: any) {
      console.error('[SubscriptionContext] VOD count increment error:', error);
      return { success: false, message: error.response?.data?.message || 'VOD sayısı artırılırken bir hata oluştu.' };
    }
  }, [isAuthenticated, fetchUsageStats]);

  const getFfmpegSettings = useCallback(async () => {
    try {
      const response = await api.get('/subscriptions/ffmpeg-settings');
      return response.data.success ? response.data.settings : { resolution: '1280x720', fps: 30 };
    } catch (error) {
      console.error('FFMPEG settings fetch error:', error);
      return { resolution: '1280x720', fps: 30 };
    }
  }, []);

  const getEditorSettings = useCallback(async () => {
    try {
      const response = await api.get('/subscriptions/editor-settings');
      return response.data.success ? response.data.settings : { resolution: '1280x720', fps: 30 };
    } catch (error) {
      console.error('Editor settings fetch error:', error);
      return { resolution: '1280x720', fps: 30 };
    }
  }, []);

  const getFreeGpuLimit = useCallback(() => FREE_TIER_GPU_LIMIT, []);

  const getGpuUsageCount = useCallback(() => {
    if (activeSubscription && activeSubscription.tier.name.toLowerCase().includes('tier 1')) {
      return tier1GpuUsageCount;
    }
    if (!activeSubscription && isAuthenticated) {
      return freeGpuJobsUsed;
    }
    return 0;
  }, [activeSubscription, isAuthenticated, tier1GpuUsageCount, freeGpuJobsUsed]);

  const incrementGpuUsage = useCallback(async () => {
    if (activeSubscription && activeSubscription.tier.name.toLowerCase().includes('tier 1')) {
      setTier1GpuUsageCount(prevCount => prevCount + 1);
    } else if (!activeSubscription && isAuthenticated) {
      if (freeGpuJobsUsed < FREE_TIER_GPU_LIMIT) {
        try {
          const response = await api.post('/user/increment-gpu-usage');
          if (response.data.success) {
            setFreeGpuJobsUsed(prevCount => prevCount + 1);
          }
        } catch (error) {
          console.error('[SubscriptionContext] Error incrementing free GPU usage on backend:', error);
        }
      }
    }
  }, [activeSubscription, isAuthenticated, freeGpuJobsUsed]);

  // Effect 1: Fetch Tiers (runs once)
  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  // Effect 2: Handles core data loading when isAuthenticated changes
  useEffect(() => {
    const loadAuthDependentData = async () => {
      // Wait for the authentication check to complete
      if (isAuthLoading) {
        setIsLoading(true);
        return;
      }

      if (isAuthenticated) {
        setIsLoading(true); // Start loading subscription data
        await fetchActiveSubscription();
        await fetchUsageStats();
        // setIsLoading(false) is now handled in the GPU data effect
      } else {
        // Not authenticated, clear all data and finish loading
        setActiveSubscription(null);
        setUsageInfo(null);
        setTier1GpuUsageCount(0);
        setFreeGpuJobsUsed(0);
        setIsLoading(false);
      }
    };
    loadAuthDependentData();
  }, [isAuthenticated, isAuthLoading, fetchActiveSubscription, fetchUsageStats]);

  // Effect 3: Handles fetching free GPU jobs and finalizes loading state
  useEffect(() => {
    const loadGpuData = async () => {
      // Only run if auth is complete and user is authenticated
      if (!isAuthLoading && isAuthenticated) {
        // This effect runs after activeSubscription might have been set by Effect 2
        await fetchFreeGpuJobsUsed();
        setIsLoading(false); // All initial data loading is complete for an authenticated user
      }
    };

    loadGpuData();
    // No specific action for !isAuthenticated here as Effect 2 handles it.
  }, [isAuthenticated, isAuthLoading, activeSubscription, fetchFreeGpuJobsUsed]);

  // Effect 4: localStorage listener for VOD limit reset
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'vodLimitReset' && event.newValue === 'true') {
        if (isAuthenticated) {
          fetchUsageStats();
        }
        localStorage.removeItem('vodLimitReset');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, fetchUsageStats]);

  return (
    <SubscriptionContext.Provider
      value={{
        tiers,
        activeSubscription,
        usageInfo,
        isLoading,
        fetchTiers,
        fetchActiveSubscription,
        fetchUsageStats,
        createSubscription,
        verifyPayment,
        cancelSubscription,
        changeTier,
        checkFeatureAccess,
        incrementClipCount,
        incrementVodCount,
        getFfmpegSettings,
        getEditorSettings,
        getGpuUsageCount,
        incrementGpuUsage,
        freeGpuJobsUsed,
        fetchFreeGpuJobsUsed,
        getFreeGpuLimit
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};